/**
 * VdiPayloadSection
 * Thin wrapper around the generic <CodeViewer> for VDI transaction payloads.
 * All editor / fullscreen / copy logic lives in CodeViewer.
 */
import React, { useMemo } from 'react';
import { FiCode } from 'react-icons/fi';
import { VscJson, VscFileCode } from 'react-icons/vsc';
import ServiceCard from '@/components/organism/Cards/ServiceCard';
import { CodeViewer } from '@/components/organism/CodeViewer';
import type { CodeViewerTab } from '@/components/organism/CodeViewer';
import type { IVDITransactionProps } from '@/utils/types/vdiTransaction';
import { formatXML } from '@/utils/xmlFormatter';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { APP_HEX } from '@/constant/theme/colors.constant';
import {
  EVDIPageAccodrionPayloadView,
  VDI_TRANSACTIONS_PAGE_PAYLOAD_ACCORDION_HEADERS,
  EVdiVersion,
} from '@/pages/VdiTransaction/constant';

// ── helpers ───────────────────────────────────────────────────────────────────
function buildPayload(row: IVDITransactionProps, payloadMessage: any): object {
  const vdiHeader = {
    vdiVersion: row.vdiVersion,
    vdiType: row.type,
    providerName: row.provider?.name,
    partnerUid: row.partnerId,
    applicationId: (row as any).applicationId,
    requestId: row.vdiTransactionId,
    operatorId: row.operatorId,
    correlationId: row.correlationId,
    transactionDtm: row.transactionDtm,
  };
  const parsed =
    typeof payloadMessage === 'string'
      ? (() => {
        try {
          return JSON.parse(payloadMessage);
        } catch {
          return { raw: payloadMessage };
        }
      })()
      : (payloadMessage ?? {});
  return { vdiHeader, ...parsed };
}

// ── props ─────────────────────────────────────────────────────────────────────
export interface VdiPayloadSectionProps {
  row: IVDITransactionProps;
  payloadMessage: any;
  testId?: string;
}

// ── component ─────────────────────────────────────────────────────────────────
export const VdiPayloadSection = ({
  row,
  payloadMessage,
  testId,
}: VdiPayloadSectionProps) => {
  const isVDIv1 = row?.vdiVersion === EVdiVersion.VDIv1;
  const rawMessage = (row as any)?.rawMessage;
  const rawPartner = (row as any)?.rawPartnerMessage;

  const tabs: CodeViewerTab[] = useMemo(() => {
    const combined = buildPayload(row, payloadMessage);
    const jsonContent = JSON.stringify(combined, undefined, 4);

    const result: CodeViewerTab[] = [
      {
        key: EVDIPageAccodrionPayloadView.JSON,
        label:
          VDI_TRANSACTIONS_PAGE_PAYLOAD_ACCORDION_HEADERS[
          EVDIPageAccodrionPayloadView.JSON
          ],
        content: jsonContent,
        language: 'json',
        dotClass: 'bg-amber-400',
        icon: <VscJson className="h-3 w-3" />,
      },
    ];

    if (isVDIv1 && rawMessage) {
      result.push({
        key: EVDIPageAccodrionPayloadView.XML,
        label:
          VDI_TRANSACTIONS_PAGE_PAYLOAD_ACCORDION_HEADERS[
          EVDIPageAccodrionPayloadView.XML
          ],
        content: formatXML(String(rawMessage), ['VDIXML'], '    ', {
          reEncodeNested: false,
        }),
        language: 'xml',
        dotClass: 'bg-cyan-400',
        icon: <VscFileCode className="h-3 w-3" />,
      });
    }

    if (isVDIv1 && rawPartner) {
      result.push({
        key: EVDIPageAccodrionPayloadView.PARTNER_XML,
        label:
          VDI_TRANSACTIONS_PAGE_PAYLOAD_ACCORDION_HEADERS[
          EVDIPageAccodrionPayloadView.PARTNER_XML
          ],
        content: formatXML(String(rawPartner), ['VDIXML'], '    ', {
          reEncodeNested: false,
        }),
        language: 'xml',
        dotClass: 'bg-violet-400',
        icon: <VscFileCode className="h-3 w-3" />,
      });
    }

    return result;
  }, [row, payloadMessage, isVDIv1, rawMessage, rawPartner]);

  return (
    <ServiceCard
      testId={testId}
      label={ORGANISM_PROPS.VDI_PAYLOAD.SECTION_LABEL}
      icon={<FiCode className="h-4 w-4" />}
      enabled
      enabledLabel={tabs[0]?.label ?? 'JSON'}
      statusPreset="statusCode"
    >
      <CodeViewer testId={"vdi-transaction"} tabs={tabs} filename="View Payload" showSearch />
    </ServiceCard>
  );
};
